import { useState } from 'react';
import { Loader2, FileText, Package, Box, ShoppingBag, RefreshCw, TrendingUp } from 'lucide-react';

const SECTION_COLOR = '#F59E0B';

const MESICE_ZKR = ['led', 'úno', 'bře', 'dub', 'kvě', 'čvn', 'čvc', 'srp', 'zář', 'říj', 'lis', 'pro'];

const fmtKc = (v) => new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 0 }).format(v || 0);
const fmtDate = (v) => { if (!v) return '—'; const d = new Date(v); return `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`; };

/**
 * Stálá karta statistik — třetí sloupec na výšku stránky (vzor RecipeCalcCard
 * u receptur). Čísla musí být vidět pořád, i při editaci formuláře, proto to
 * není záložka.
 *
 * Graf je JEDNA série (měsíční nákupy), takže nese barvu sekce a legendu
 * nepotřebuje — nadpis ji pojmenuje. Roste do zbylé výšky sloupce; hodnoty se
 * nepíšou nad každý sloupec, číslo ukáže hover a nejsilnější měsíc je popsaný.
 */
export default function DodavatelStatistiky({ data, loading, chyba, isDark, mutedColor, secondaryColor, cardStyle, onOpenDoklad, onOtevritSortiment}) {
    const [hover, setHover] = useState(null);

    const tileBg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)';
    const tileBorder = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)';
    const border = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';

    // Karta drží celou výšku sloupce; obsah uvnitř scrolluje, graf roste do zbytku.
    const Obal = ({ children, podtitulek }) => (
        <div className="rounded-xl flex flex-col" style={{ ...cardStyle, flex: 1, minHeight: 0 }}>
            <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: `1px solid ${border}`, flexShrink: 0 }}>
                <span className="text-xs font-bold uppercase tracking-wide" style={{ color: secondaryColor }}>Statistiky</span>
                {podtitulek && <span className="text-[10px]" style={{ color: mutedColor }}>{podtitulek}</span>}
            </div>
            <div className="p-4 flex flex-col" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>{children}</div>
        </div>
    );

    if (loading) {
        return <Obal><div className="flex justify-center items-center" style={{ flex: 1 }}><Loader2 size={20} className="animate-spin" style={{ color: mutedColor }} /></div></Obal>;
    }
    if (chyba) {
        return (
            <Obal>
                <div className="text-center py-6">
                    <p className="text-xs" style={{ color: '#EF4444' }}>{chyba}</p>
                    <p className="text-[10px] mt-1" style={{ color: mutedColor }}>Zkus kartu otevřít znovu.</p>
                </div>
            </Obal>
        );
    }
    if (!data) return null;

    const { doklady, mesicni = [], sortiment = {}, obaly } = data;
    // Vratné obaly: od 3.482 celý rozpad per typ, ne jen součet. Samostatná
    // záložka „Vratné obaly" zanikla — kolik kegů u dodavatele držíme je jeho
    // vlastnost, ne agenda, a tady to má kontext (obrat, splatnost, sortiment).
    const obalyRadky = (obaly?.radky || []).filter(r => Math.abs(r.stav) > 0.005);
    const obalySouhrn = obaly?.souhrn;
    const maxCastka = Math.max(...mesicni.map(m => m.castka), 0);
    const maxIdx = mesicni.findIndex(m => m.castka === maxCastka && maxCastka > 0);
    const bezNakupu = !doklady || doklady.pocet === 0;

    const kpi = (hodnota, jednotka, popis) => (
        <div className="rounded-lg px-3 py-2.5" style={{ background: tileBg, border: `1px solid ${tileBorder}` }}>
            <div className="text-lg font-bold leading-none tabular-nums" style={{ color: 'var(--text-primary)' }}>
                {hodnota}{jednotka && <span className="text-[11px] font-normal ml-0.5" style={{ color: mutedColor }}>{jednotka}</span>}
            </div>
            <div className="text-[10px] mt-1" style={{ color: mutedColor }}>{popis}</div>
        </div>
    );

    return (
        <Obal podtitulek={doklady.pocet > 0 ? 'posledních 12 měsíců' : 'zatím bez nákupů'}>
            {/* Hlavní číslo */}
            <div className="mb-4" style={{ flexShrink: 0 }}>
                <div className="flex items-baseline gap-1.5">
                    <span className="text-3xl font-bold tabular-nums leading-none" style={{ color: 'var(--text-primary)' }}>
                        {fmtKc(doklady.obrat_12m)}
                    </span>
                    <span className="text-sm font-medium" style={{ color: mutedColor }}>Kč</span>
                </div>
                <p className="text-[11px] mt-1.5" style={{ color: mutedColor }}>
                    obrat bez DPH
                    {doklady.podil_12m_pct != null && (
                        <> · <span style={{ color: SECTION_COLOR, fontWeight: 600 }}>{String(doklady.podil_12m_pct).replace('.', ',')} %</span> všech nákupů</>
                    )}
                </p>
            </div>

            {/* KPI mřížka */}
            <div className="grid grid-cols-2 gap-2 mb-4" style={{ flexShrink: 0 }}>
                {kpi(doklady.pocet, '', 'dokladů celkem')}
                {kpi(fmtKc(doklady.prumer_doklad), ' Kč', 'průměr na doklad')}
                {kpi(fmtKc(doklady.obrat_bez_dph), ' Kč', 'obrat celkem')}
                {kpi(doklady.posledni_datum ? fmtDate(doklady.posledni_datum).replace(/ /g, '') : '—', '', 'poslední nákup')}
            </div>

            {/* Měsíční nákupy — roste do zbylé výšky sloupce */}
            <div className="flex flex-col" style={{ flex: 1, minHeight: 140 }}>
                <div className="flex items-center gap-1.5 mb-2" style={{ flexShrink: 0 }}>
                    <TrendingUp size={13} style={{ color: mutedColor }} />
                    <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: mutedColor }}>Nákupy po měsících</span>
                </div>

                {bezNakupu ? (
                    <div className="flex flex-col items-center justify-center text-center" style={{ flex: 1 }}>
                        <FileText size={22} style={{ color: mutedColor, marginBottom: 8 }} />
                        <p className="text-xs" style={{ color: mutedColor }}>Zatím žádný schválený doklad</p>
                    </div>
                ) : (
                    <>
                        <div className="flex items-end gap-[3px]" style={{ flex: 1, minHeight: 90 }}>
                            {mesicni.map((m, i) => {
                                const podil = maxCastka > 0 ? m.castka / maxCastka : 0;
                                const aktivni = hover === i;
                                return (
                                    <div key={m.mesic} className="flex-1 flex flex-col justify-end items-center" style={{ height: '100%' }}
                                        onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
                                        {(i === maxIdx || aktivni) && m.castka > 0 && (
                                            <span className="text-[10px] font-semibold tabular-nums mb-1 whitespace-nowrap"
                                                style={{ color: aktivni ? 'var(--text-primary)' : mutedColor }}>
                                                {fmtKc(m.castka)}
                                            </span>
                                        )}
                                        <div style={{
                                            width: '100%',
                                            height: `${Math.max(podil * 100, m.castka > 0 ? 3 : 1)}%`,
                                            background: m.castka > 0
                                                ? (aktivni ? SECTION_COLOR : `${SECTION_COLOR}${isDark ? 'CC' : 'B3'}`)
                                                : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'),
                                            borderRadius: '4px 4px 0 0',
                                            transition: 'background 0.12s',
                                        }} />
                                    </div>
                                );
                            })}
                        </div>
                        <div className="flex gap-[3px] mt-1.5" style={{ borderTop: `1px solid ${border}`, paddingTop: 5, flexShrink: 0 }}>
                            {mesicni.map((m, i) => {
                                const [, mm] = m.mesic.split('-');
                                const leden = mm === '01';
                                return (
                                    <span key={m.mesic} className="flex-1 text-center text-[10px]"
                                        style={{ color: hover === i ? 'var(--text-primary)' : mutedColor, fontWeight: leden ? 700 : 400 }}
                                        title={m.mesic}>
                                        {MESICE_ZKR[parseInt(mm, 10) - 1]}
                                    </span>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>

            {/* Sortiment z ceníků */}
            <div className="mt-4" style={{ flexShrink: 0 }}>
                <p className="text-[11px] font-semibold uppercase tracking-wide mb-2" style={{ color: mutedColor }}>Co od něj bereme</p>
                <div className="grid grid-cols-3 gap-2">
                    {[
                        { Icon: Package, value: sortiment.suroviny ?? 0, label: 'surovin', typ: 'MAT' },
                        { Icon: Box, value: sortiment.obaly ?? 0, label: 'obalů', typ: 'OBA' },
                        { Icon: ShoppingBag, value: sortiment.spotrebni ?? 0, label: 'spotř. zboží', typ: 'SPOZ' },
                    ].map(({ Icon, value, label, typ }) => {
                        // Proklik do Špižírny na jeho sortiment — jen když tam něco je.
                        const lze = value > 0 && !!onOtevritSortiment;
                        return (
                            <button key={label} disabled={!lze}
                                onClick={() => lze && onOtevritSortiment(typ)}
                                title={lze ? `Zobrazit ve Špižírně (${label} od tohoto dodavatele)` : undefined}
                                className="rounded-lg py-2.5 flex flex-col items-center gap-1 w-full"
                                style={{
                                    background: tileBg, border: `1px solid ${tileBorder}`,
                                    cursor: lze ? 'pointer' : 'default', transition: 'border-color 0.15s',
                                }}
                                onMouseEnter={e => { if (lze) e.currentTarget.style.borderColor = SECTION_COLOR; }}
                                onMouseLeave={e => { e.currentTarget.style.borderColor = tileBorder.replace('1px solid ', ''); }}>
                                <Icon size={15} style={{ color: value > 0 ? SECTION_COLOR : mutedColor }} />
                                <div className="text-base font-bold leading-none tabular-nums" style={{ color: 'var(--text-primary)' }}>{value}</div>
                                <div className="text-[9px] text-center" style={{ color: mutedColor }}>{label}</div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {obalyRadky.length > 0 && (
                <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${border}`, flexShrink: 0 }}>
                    <div className="flex items-baseline gap-2 mb-2">
                        <span className="text-[11px]" style={{ color: secondaryColor }}>Vratné obaly</span>
                        <span className="text-[11px] font-semibold ml-auto tabular-nums" style={{ color: 'var(--text-primary)' }}>
                            {fmtKc(obalySouhrn?.zaparkovano_kc)} Kč
                        </span>
                    </div>
                    {obalyRadky.map((r, i) => (
                        <div key={i} className="flex items-baseline gap-2 py-[3px]">
                            <Box size={11} style={{ color: r.v_ciselniku ? SECTION_COLOR : '#F59E0B', flexShrink: 0 }} />
                            <span className="text-[11px] truncate" style={{ color: 'var(--text-primary)' }}
                                title={r.v_ciselniku ? undefined : 'Typ obalu není v číselníku — záloha se bere z dokladu'}>
                                {r.obal_nazev}
                            </span>
                            <span className="text-[10.5px] tabular-nums ml-auto flex-shrink-0" style={{ color: r.stav < 0 ? '#EF4444' : mutedColor }}
                                title={r.stav < 0 ? 'Záporný stav — vrátili jsme víc, než systém eviduje' : `výdej ${r.vydej} · vrácení ${r.vraceni}`}>
                                {r.stav} ks
                            </span>
                            <span className="text-[10.5px] tabular-nums flex-shrink-0" style={{ color: secondaryColor, minWidth: 62, textAlign: 'right' }}>
                                {fmtKc(r.zaparkovano_kc)}
                            </span>
                        </div>
                    ))}
                    <p className="text-[9.5px] mt-1.5" style={{ color: mutedColor }}>
                        Zálohy jsou pohledávka vůči dodavateli — ne náklad a ne předmět DPH,
                        takže do Výsledovky ani food costu nevstupují.
                    </p>
                </div>
            )}

            {/* Poslední doklad */}
            {doklady.posledni_doklad && (
                <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${border}`, flexShrink: 0 }}>
                    <p className="text-[10px] mb-1.5" style={{ color: mutedColor }}>Poslední doklad</p>
                    <div onClick={() => onOpenDoklad?.(doklady.posledni_doklad.id)} style={{ cursor: onOpenDoklad ? 'pointer' : 'default' }}>
                        <div className="flex items-center gap-2">
                            <FileText size={13} style={{ color: SECTION_COLOR, flexShrink: 0 }} />
                            <span className="text-[12px] font-mono font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                                {doklady.posledni_doklad.interni_cislo || `#${doklady.posledni_doklad.id}`}
                            </span>
                            <span className="text-[12px] ml-auto font-semibold tabular-nums shrink-0" style={{ color: 'var(--text-primary)' }}>
                                {fmtKc(doklady.posledni_doklad.castka_s_dph)} Kč
                            </span>
                        </div>
                        <p className="text-[10px] mt-0.5" style={{ color: mutedColor }}>{fmtDate(doklady.posledni_doklad.datum)}</p>
                    </div>
                </div>
            )}

            {/* ARES razítko — až úplně dole */}
            <div className="mt-auto pt-3 flex items-center gap-1.5" style={{ borderTop: `1px solid ${border}`, flexShrink: 0 }}>
                <RefreshCw size={11} style={{ color: mutedColor, flexShrink: 0 }} />
                <span className="text-[10px]" style={{ color: mutedColor }}>
                    ARES: {data.ares_aktualizovano ? fmtDate(data.ares_aktualizovano) : 'nikdy'}
                </span>
            </div>
        </Obal>
    );
}
